import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonContent,
  IonIcon,
  IonItem,
  IonLoading,
  IonPage,
  IonToast
} from "@ionic/react";
import Header from "../components/Header";
import {ROLE} from "../../enums/ROLE";
import {AuthContext} from "../../providers/authContext";
import {receiptOutline} from "ionicons/icons";
import yoneticiService from "../../services/YoneticiService";
import {logError, parseTurkishNumber} from "../../utils";
import {DataTable, DataTablePFSEvent} from "primereact/datatable";
import {Column} from "primereact/column";
import "./YoneticiTahsilatGozlem.css";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {TahsilatTable} from "../entities/TahsilatTable";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";

const YoneticiCariEkstre: React.FC = () => {
  const { kurumBilgileri, logo } = React.useContext(AuthContext);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [cariFilterValue, setCariFilterValue] = useState<string>('');
  const [cariEkstreListesi, setCariEkstreListesi] = useState<TahsilatTable[]>([]);
  const [filtrelenmisCariEkstreVerisi, setfiltrelenmisCariEkstreVerisi] = useState(cariEkstreListesi || []);
  const cariEkstreTable = useRef<DataTable>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10); // varsayılan sayfa boyutu

  const onPageChange = (e: DataTablePFSEvent) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setShowLoading(true);
    yoneticiService
      .getCariEkstreListesi()
      .then((data: TahsilatTable[]) => {
        setCariEkstreListesi(data);
        setfiltrelenmisCariEkstreVerisi(data);
      })
      .catch((error: any) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      })
      .finally(() => setShowLoading(false));
  }

  const cariFilter = <InputText value={cariFilterValue}
                                onChange={(e) => {
                                  cariEkstreTable.current?.filter(e.target.value, 'adiSoyadi', 'contains');
                                  setCariFilterValue(e.target.value);
                                }} placeholder="Cari Hesap" style={{width: '100%'}}/>

  const sayfaToplami = useMemo(() => {
    const veri = filtrelenmisCariEkstreVerisi ?? cariEkstreListesi ?? [];
    return veri
      .slice(first, first + rows)
      .reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisCariEkstreVerisi, cariEkstreListesi, first, rows]);

  const genelToplam = useMemo(() => {
    const veri = filtrelenmisCariEkstreVerisi ?? cariEkstreListesi ?? [];
    return veri.reduce((toplam, item) => toplam + (parseTurkishNumber(item.tutar) || 0), 0);
  }, [filtrelenmisCariEkstreVerisi, cariEkstreListesi]);

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="TOPLAM" footerStyle={{ textAlign: 'right' }} />
        <Column
          footer={sayfaToplami.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
          footerStyle={{ textAlign: 'right' }}
        />
      </Row>
      <Row>
        <Column footer="GENEL TOPLAM" footerStyle={{ textAlign: 'right' }} />
        <Column
          footer={genelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
          footerStyle={{ textAlign: 'right' }}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <IonPage>
      <Header
        title={kurumBilgileri?.kisaTanim}
        subtitle=""
        logo={logo}
        roles={[ROLE.Yonetici]}
      />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={"Lütfen Bekleyin..."}
        />
        <IonToast
          isOpen={showToastMessage !== undefined}
          onDidDismiss={() => setShowToastMessage(undefined)}
          message={showToastMessage}
          duration={5000}
          position="top"
        />
        <IonCard>
          <IonCardHeader>
            <IonItem color="light">
              <IonIcon icon={receiptOutline} slot="start" />
              Cari Ekstre
            </IonItem>
          </IonCardHeader>
          <IonCardContent>
            <DataTable ref={cariEkstreTable} value={cariEkstreListesi}
                       paginator rows={rows} rowsPerPageOptions={[10, 25, 50]} first={first}
                       paginatorTemplate={{
                         // Hangi bileşenlerin hangi sırayla görüneceğini belirler
                         layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',

                         // RowsPerPageDropdown bileşenini tamamen baştan yazıyoruz
                         'RowsPerPageDropdown': (options) => {
                           return (
                             <div className="flex align-items-center" style={{ gap: '0.5rem', margin: '0 auto' }}>
                               <span style={{ fontSize: '14px', color: '#6c757d' }}>Sayfada</span>

                               <Dropdown
                                 value={options.value}
                                 options={[10, 25, 50]}
                                 onChange={options.onChange}
                                 /* Genişliği kısıtlayan en önemli kısım */
                                 style={{ width: '5rem' }}
                               />

                               <span style={{ fontSize: '14px', color: '#6c757d', paddingLeft: '0.5rem' }}>kayıt göster</span>
                             </div>
                           );
                         }
                       }}
                       onPage={onPageChange}
                       className="p-datatable-sm tahsilat-table"
                       footerColumnGroup={footerGroup}
                       onValueChange={(e) => setfiltrelenmisCariEkstreVerisi(e)}
                       stripedRows filterDisplay="row" showGridlines
                       scrollable
                       scrollDirection="horizontal"
                       emptyMessage="Tabloda herhangi bir veri mevcut değil">
              <Column field="adiSoyadi" header="CARİ HESAP" headerClassName="ion-justify-content-center"
                      filter
                      filterElement={cariFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false}
                      headerStyle={{ width: '75%' }} bodyStyle={{ width: '75%' }} />
              <Column field="tutar" header="TUTAR" bodyClassName="ion-justify-content-end" headerClassName="ion-justify-content-center" />
            </DataTable>

          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  )
};

export default YoneticiCariEkstre;
